import { FC, ReactNode } from 'react'
import { EntryImageNode } from 'gql'
import { getImageUrl } from '~utils'
import { useScreenClass } from '~hooks'
import { GrantedSecure, Markdown } from '~shared'
import { Typography, Heading, Image, Grid } from '~ui'
import classNames from 'classnames'

import styles from './Side.module.scss'

const { Paragraph } = Typography

type SidebarProps = {
  title: string
  info?: string
  children?: ReactNode
  image: Pick<EntryImageNode, 'path' | 'extension'>
  spotifyWidgetLink?: string
  after?: ReactNode
}

const Sidebar: FC<SidebarProps> = ({ title, image, info, children, after, spotifyWidgetLink }) => {
  const { isMobile } = useScreenClass()

  return (
    <div className={styles.wrapper}>
      {isMobile ? (
        <>
          <div className={classNames(styles['section-mobile'])}>
            <div className={styles['image-container']}>
              <Image width={100} height={100} src={getImageUrl(image, 200, 200)} alt={title} />
            </div>
            <Grid gap={20}>
              <Heading Tag="h2" size="h3" uppercase fontWeight={400} colorGlow="neon-purple">
                {title}
              </Heading>
              <div style={{ width: 'fit-content' }}>{children}</div>
            </Grid>
          </div>
          {after && <div style={{ marginTop: 20 }}>{after}</div>}
        </>
      ) : (
        <>
          <div className={styles.image}>
            <img src={getImageUrl(image, 600, 400)} alt={title} />
          </div>

          <div className={styles.content}>
            <Heading Tag="h2" size="h1">
              {title}
            </Heading>
            {children}
            {info && (
              <div className={styles.about}>
                <Paragraph size="body" lineHeight="body" color="g-gray-1">
                  <Markdown md={info} />
                </Paragraph>
              </div>
            )}

            {spotifyWidgetLink && (
              <div className={styles['spotify-widget']}>
                <iframe
                  title="spotify widget"
                  src={spotifyWidgetLink}
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen={false}
                  frameBorder="0"
                  width="100%"
                  height="380"
                />
              </div>
            )}

            <GrantedSecure withPadding={false} />
          </div>
        </>
      )}
    </div>
  )
}

export default Sidebar
